import { ShellHeader } from '@/app/shell-header';
import { useOperatorSession } from '@/auth/runtime';
import { IdText } from '@/components/id-text';
import { LoadFailure } from '@/components/load-failure';
import { UnsavedGuard } from '@/components/unsaved-guard';
import { isUuid } from '@/lib/validation';
import { Button, DetailPage, PageHeader } from '@mgrvtechgroup/admin-ds';
import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { UserAuthorizationSection } from './user-authorization-section';
import {
  hasUserAccess,
  hasUserAuthorizationAccess,
  hasUserProfileCommandAccess,
  userDetailQuery,
  userRuntime,
  type UserRuntime,
} from './user-queries';
import { UserStatusSection } from './user-status-section';
import { userDetailState } from './user-url';

export function UserDetailPage({ runtime = userRuntime }: { runtime?: UserRuntime }) {
  const { userId = '' } = useParams();
  const session = useOperatorSession(runtime.session);
  // 사용자나 로그인 세대가 바뀌면 초안과 진행 중 요청을 이어 받지 않는다.
  return <UserDetailContent key={`${userId}:${String(session.generation)}`} runtime={runtime} />;
}

function UserDetailContent({ runtime }: { runtime: UserRuntime }) {
  const [statusDirty, setStatusDirty] = useState(false);
  const [authorizationDirty, setAuthorizationDirty] = useState(false);
  const { userId = '' } = useParams();
  const [params] = useSearchParams();
  const route = userDetailState(params);
  const session = useOperatorSession(runtime.session);
  const allowed = hasUserAccess(runtime.session, session.generation);
  const valid = isUuid(userId) && userId === userId.toLowerCase() && route.valid;
  const user = useQuery(userDetailQuery(runtime, session.generation, userId, valid && allowed));
  const frame = (content: ReactNode) => (
    <DetailPage className="min-h-0 flex-1 overflow-y-auto">
      <ShellHeader>
        <PageHeader
          title="사용자 상세"
          description="계정 상태와 서비스 역할을 확인하고 필요한 변경을 저장합니다."
          className="items-start sm:items-center"
        />
      </ShellHeader>
      <DetailPage.Body className="min-h-0">
        <Button
          nativeButton={false}
          role="link"
          variant="outline"
          render={<Link to={route.returnTo} />}
        >
          사용자 목록으로
        </Button>
        <div className="space-y-1 rounded-md border p-3">
          <p className="font-medium">사용자 {userId.slice(0, 8)}</p>
          <details className="break-all">
            <summary className="cursor-pointer">사용자 식별 정보</summary>
            <IdText value={userId} label="사용자 ID" length={36} />
          </details>
        </div>
        {content}
      </DetailPage.Body>
    </DetailPage>
  );
  if (session.status !== 'authorized') return null;
  if (!valid)
    return frame(<p role="alert">사용자 주소가 올바르지 않습니다. 목록에서 다시 선택해 주세요.</p>);
  if (!allowed) return frame(<p role="alert">현재 사용자 조회 권한이 없습니다.</p>);
  if (user.isPending) return frame(<p role="status">사용자 정보를 조회하고 있습니다.</p>);
  if (user.isError)
    return frame(
      <LoadFailure error={user.error} subject="사용자" onRetry={() => void user.refetch()} />,
    );
  if (user.data.user_id !== userId)
    return frame(<p role="status">현재 사용자를 확인하고 있습니다.</p>);
  const canAuthorize = hasUserAuthorizationAccess(runtime.session, session.generation);
  const canChangeStatus = hasUserProfileCommandAccess(runtime.session, session.generation);
  return frame(
    <>
      <UnsavedGuard when={statusDirty || authorizationDirty} />
      <UserStatusSection
        runtime={runtime}
        userId={userId}
        canEdit={canChangeStatus}
        onDirtyChange={setStatusDirty}
      />
      {canAuthorize ? (
        <UserAuthorizationSection
          runtime={runtime}
          userId={userId}
          canEdit
          onDirtyChange={setAuthorizationDirty}
        />
      ) : (
        <DetailPage.Section title="역할과 권한">
          <p>
            현재 서비스 역할: {user.data.role_bits === null ? '권한 미배정' : '확인됨'} · 권한
            조회에는 사용자 권한 관리 권한이 필요합니다.
          </p>
        </DetailPage.Section>
      )}
    </>,
  );
}
